import { Icone, Paleta, type ItemPaleta } from '@persona/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FileText, Home, LayoutGrid, LogOut, Moon, Plus, Stethoscope, Sun, SunMoon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../src/api/cliente';
import { buscar, semAcento, type ItemIndice } from '../src/navegacao/busca';
import { useIndice } from '../src/navegacao/indice';
import { useFavoritos, useRecentes } from '../src/navegacao/preferencias';
import { useSessao } from '../src/sessao/sessao';
import { useEscolhaTema } from './Topo';

interface RegistroEncontrado { rotina: string; rotinaNome: string; alias: string; id: number; titulo: string; subtitulo: string | null }

interface AcaoSistema {
  id: string;
  titulo: string;
  palavras: string[];
  icone: React.ReactNode;
  executar: () => void;
}

/**
 * Ctrl+K (07 §2.5): rotinas, "Novo …" de cadastros com incluir, módulos e ações do sistema, com o ranking do índice.
 * Prefixos: '>' só ações, '/' só rotinas e menus. Enter abre; Alt+Enter favorita. Registros vêm de `GET /busca` (D1-15): a partir de 3
 * caracteres, só das rotinas que o usuário vê e no escopo dele — o servidor decide; o cliente não guarda índice de registros.
 */
export function PaletaGlobal({ aberta, busca, aoBuscar, aoFechar }: { aberta: boolean; busca: string; aoBuscar: (t: string) => void; aoFechar: () => void }) {
  const indice = useIndice();
  const { favoritos, alternar } = useFavoritos();
  const { recentes } = useRecentes();
  const navegar = useNavigate();
  const { sair } = useSessao();
  const [, escolherTema] = useEscolhaTema();
  const { empresa, filial } = useSessao();
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTermo(/^[>/]/.test(busca.trim()) ? '' : busca.trim()), 220);
    return () => clearTimeout(t);
  }, [busca]);
  const registros = useQuery({
    queryKey: ['busca', empresa?.codigo, filial?.codigo, termo],
    queryFn: ({ signal }) => api.get<RegistroEncontrado[]>(`/busca?q=${encodeURIComponent(termo)}`, signal),
    enabled: aberta && termo.length >= 3,
    staleTime: 15_000,
  });

  const acoes: AcaoSistema[] = useMemo(() => [
    { id: 'a:inicio', titulo: 'Ir para o início', palavras: ['inicio', 'home'], icone: <Home size={16} />, executar: () => void navegar({ to: '/' }) },
    { id: 'a:tema-escuro', titulo: 'Tema escuro', palavras: ['tema', 'escuro', 'dark'], icone: <Moon size={16} />, executar: () => escolherTema('escuro') },
    { id: 'a:tema-claro', titulo: 'Tema claro', palavras: ['tema', 'claro', 'light'], icone: <Sun size={16} />, executar: () => escolherTema('claro') },
    { id: 'a:tema-sistema', titulo: 'Tema do sistema', palavras: ['tema', 'sistema'], icone: <SunMoon size={16} />, executar: () => escolherTema('sistema') },
    { id: 'a:diagnostico', titulo: 'Diagnóstico', palavras: ['diagnostico', 'saude', 'licenca'], icone: <Stethoscope size={16} />, executar: () => void navegar({ to: '/diagnostico' }) },
    { id: 'a:sair', titulo: 'Sair', palavras: ['sair', 'logout'], icone: <LogOut size={16} />, executar: () => void sair().then(() => navegar({ to: '/entrar', search: { volta: undefined } })) },
  ], [navegar, escolherTema, sair]);

  const itens = useMemo<ItemPaleta[]>(() => {
    const lista = indice.data ?? [];
    const porCodigo = new Map(lista.map((i) => [i.codigo, i]));
    const rotina = (i: ItemIndice, secao: string): ItemPaleta => ({ id: `r:${i.codigo}`, tipo: 'rotina', titulo: i.nome, codigo: i.codigo, trilha: i.caminho, icone: <Icone nome={i.icone} size={16} />, secao });
    const texto = busca.trim();

    if (!texto) {
      return [
        ...recentes.map((c) => porCodigo.get(c)).filter((i): i is ItemIndice => !!i).slice(0, 8).map((i) => ({ ...rotina(i, 'Recentes'), id: `rec:${i.codigo}` })),
        ...favoritos.map((c) => porCodigo.get(c)).filter((i): i is ItemIndice => !!i).map((i) => ({ ...rotina(i, 'Favoritos'), id: `fav:${i.codigo}` })),
        ...acoes.slice(0, 3).map((a) => ({ id: a.id, tipo: 'acao' as const, titulo: a.titulo, icone: a.icone, secao: 'Ações' })),
      ];
    }
    if (texto.startsWith('>')) {
      const q = semAcento(texto.slice(1).trim());
      return acoes.filter((a) => !q || semAcento(a.titulo).includes(q) || a.palavras.some((p) => p.startsWith(q)))
        .map((a) => ({ id: a.id, tipo: 'acao' as const, titulo: a.titulo, icone: a.icone, secao: 'Ações' }));
    }
    const soRotinas = texto.startsWith('/');
    const q = soRotinas ? texto.slice(1) : texto;
    const achadas = buscar(lista, q, favoritos, recentes, 20);
    const resultado: ItemPaleta[] = achadas.map((i) => rotina(i, 'Rotinas'));
    if (!soRotinas) {
      const qn = semAcento(q);
      // "Novo cliente": o verbo casa com "novo"/"incluir" e o resto com a rotina.
      const querNovo = /^(novo|nova|incluir)\b/.test(qn);
      const alvoNovo = querNovo ? qn.replace(/^(novo|nova|incluir)\s*/, '') : qn;
      const novos = (querNovo ? buscar(lista.filter((i) => i.podeIncluir), alvoNovo || 'a', favoritos, recentes, 5) : achadas.filter((i) => i.podeIncluir).slice(0, 3))
        .map((i): ItemPaleta => ({ id: `n:${i.codigo}`, tipo: 'cadastro', titulo: `Novo — ${i.nome}`, codigo: i.codigo, trilha: i.caminho, icone: <Plus size={16} />, secao: 'Incluir' }));
      const modulos = [...new Map(lista.map((i) => [i.modulo, i])).values()]
        .filter((i) => semAcento(i.moduloNome).startsWith(qn) || i.modulo.toLowerCase() === qn)
        .map((i): ItemPaleta => ({ id: `m:${i.modulo}`, tipo: 'modulo', titulo: i.moduloNome, codigo: i.modulo, icone: <LayoutGrid size={16} />, secao: 'Módulos' }));
      const acoesAchadas = acoes.filter((a) => semAcento(a.titulo).includes(qn) || a.palavras.some((p) => p.startsWith(qn)))
        .map((a): ItemPaleta => ({ id: a.id, tipo: 'acao', titulo: a.titulo, icone: a.icone, secao: 'Ações' }));
      const achados = (termo.length >= 3 && termo === q.trim() ? registros.data ?? [] : []).map((r): ItemPaleta => ({
        id: `g:${r.rotina}:${r.id}`, tipo: 'registro', titulo: r.titulo, codigo: r.rotina, trilha: [r.rotinaNome, r.subtitulo].filter(Boolean).join(' · '),
        icone: <FileText size={16} />, secao: 'Registros',
      }));
      return querNovo ? [...novos, ...resultado] : [...resultado, ...achados, ...novos, ...modulos, ...acoesAchadas];
    }
    return resultado;
  }, [busca, indice.data, favoritos, recentes, acoes, termo, registros.data]);

  const escolher = (item: ItemPaleta, e: { novaAba: boolean; favoritar: boolean }) => {
    const [tipo, valor] = [item.id.slice(0, item.id.indexOf(':')), item.id.slice(item.id.indexOf(':') + 1)];
    if (e.favoritar && item.codigo && item.tipo === 'rotina') {
      alternar(item.codigo);
      return;
    }
    aoFechar();
    if (tipo === 'a') acoes.find((a) => a.id === item.id)?.executar();
    else if (tipo === 'm') void navegar({ to: '/m/$modulo', params: { modulo: valor.toLowerCase() } });
    else if (tipo === 'n') void navegar({ to: '/r/$rotina/novo', params: { rotina: valor } });
    else if (tipo === 'g') {
      const [rotina, id] = valor.split(':');
      void navegar({ to: '/r/$rotina/$id', params: { rotina, id } });
    }
    else if (item.codigo) void navegar({ to: '/r/$rotina', params: { rotina: item.codigo } });
  };

  return (
    <Paleta aberta={aberta} aoFechar={aoFechar} busca={busca} aoBuscar={aoBuscar} itens={itens} aoEscolher={escolher}
      vazio={indice.isLoading || registros.isFetching ? 'Buscando…' : 'Nada encontrado. Tente o código da rotina, parte do nome, um registro (3+ letras) ou > para ações.'} />
  );
}
